// ===========================================
// Échéances — Liste des contrats arrivant à échéance (relance / renouvellement)
// UX aligné sur la page Impayés (KPIs + filtres + table + export)
// ===========================================
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Select, Button, Tag, Grid, message, Dropdown, Empty,
    Modal, Checkbox, List, Pagination, Spin, Tooltip, DatePicker,
} from 'antd';
import {
    SearchOutlined, DownloadOutlined, FilePdfOutlined, FileExcelOutlined,
    CalendarOutlined, ClockCircleOutlined, FieldTimeOutlined, WarningOutlined,
    ClearOutlined, PhoneOutlined, UserOutlined, BankOutlined,
    QuestionCircleOutlined, CloseOutlined, ReloadOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { contratsService, agencesService, typesAssuranceService } from '../../common/services';
import { useDebounce, useReferentiel, useGlobalAgenceState, useCanSwitchAgence, useUserAgenceId, useExporterName } from '../../common/hooks';
import { resolveAgenceLabel, addExporterFooter, PDF_HEAD_STYLES, PDF_FOOT_STYLES } from '../../common/utils/pdfExport';
import { borderExcelTable, addExcelLogoHeader, addExcelExportInfo } from '../../common/utils/excelExport';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatMontant, formatMontantPdf, formatPhone as formatPhoneShared } from '../../common/utils/format';

import MiniKpiCard from '../clients/components/MiniKpiCard';
import Can from '../../components/can';

import './echeances.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

const formatPhone = (tel) => formatPhoneShared(tel, { fallback: '—' });

const DAY = 1000 * 60 * 60 * 24;
// Jours restants avant échéance : négatif = déjà échu (retard).
const joursRestants = (dateEcheance) => {
    if (!dateEcheance) return null;
    return Math.ceil((new Date(dateEcheance).getTime() - Date.now()) / DAY);
};

const clientName = (c) => {
    if (!c) return '—';
    return `${c.nom || ''} ${c.prenom || ''}`.trim() || c.code || '—';
};

// Métadonnées d'affichage selon le nombre de jours restants.
const echeanceMeta = (jours) => {
    if (jours === null) return { color: 'default', label: '—', tone: 'gold' };
    if (jours < 0) return { color: 'red', label: `Échu (${Math.abs(jours)} j)`, tone: 'red' };
    if (jours === 0) return { color: 'red', label: "Aujourd'hui", tone: 'red' };
    if (jours <= 7) return { color: 'orange', label: `${jours} j`, tone: 'orange' };
    if (jours <= 15) return { color: 'gold', label: `${jours} j`, tone: 'gold' };
    return { color: 'green', label: `${jours} j`, tone: 'green' };
};

const WINDOW_OPTIONS = [
    { value: '7', label: '≤ 7 jours' },
    { value: '15', label: '≤ 15 jours' },
    { value: '30', label: '≤ 30 jours' },
    { value: '60', label: '≤ 60 jours' },
    { value: '90', label: '≤ 90 jours' },
    { value: 'all', label: 'Toutes' },
];

const Echeances = () => {
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    // ─── État ─────────────────────────────────────────────────────────
    const [echeances, setEcheances] = useState([]);
    const [guideOpen, setGuideOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 400);
    const [filterAgence, setFilterAgence] = useGlobalAgenceState();
    const canSwitchAgence = useCanSwitchAgence();
    const userAgenceId = useUserAgenceId();
    const exporterName = useExporterName();
    const [jours, setJours] = useState('30');
    const [inclureEchus, setInclureEchus] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [filterDates, setFilterDates] = useState(null);
    // Une période d'échéance explicite remplace la fenêtre glissante et « Inclure les échus »
    const hasPeriode = !!(filterDates && filterDates[0] && filterDates[1]);
    const { data: agences } = useReferentiel('agences', () => agencesService.getAll());
    const { data: typesAssurance } = useReferentiel('typesAssurance', () => typesAssuranceService.getAll());
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [serverStats, setServerStats] = useState({ echus: 0, urgent7: 0, dans30: 0 });

    const buildParams = (page, pageSize) => ({
        page, pageSize,
        q: debouncedSearch,
        agence: filterAgence,
        typeAssurance: filterType !== 'all' ? filterType : undefined,
        start: hasPeriode ? filterDates[0].format('YYYY-MM-DD') : undefined,
        end: hasPeriode ? filterDates[1].format('YYYY-MM-DD') : undefined,
        jours,
        inclureEchus: inclureEchus ? 'true' : 'false',
    });

    const fetchEcheances = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const res = await contratsService.getEcheancesListe(buildParams(page, pageSize));
            const payload = res?.data || res || {};
            const data = Array.isArray(payload) ? payload : payload.items || [];
            setEcheances(data);
            setPagination((prev) => ({ ...prev, current: page, pageSize, total: payload.total ?? data.length }));
            if (payload.stats) setServerStats(payload.stats);
        } catch (error) {
            toast.error('Erreur lors du chargement des échéances');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEcheances(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filterAgence, filterType, filterDates, jours, inclureEchus]);

    // ─── KPIs ─────────────────────────────────────────────────────────
    // Total = périmètre filtré courant ; échus / ≤7j / ≤30j = indicateurs
    // stables de l'agence, calculés côté serveur (indépendants de la fenêtre).
    const stats = useMemo(() => ({
        total: pagination.total,
        echus: serverStats.echus,
        urgent7: serverStats.urgent7,
        dans30: serverStats.dans30,
    }), [pagination.total, serverStats]);

    // ─── Filtres ──────────────────────────────────────────────────────
    const activeFiltersCount = useMemo(() => {
        let n = 0;
        if (filterAgence !== 'all') n++;
        if (jours !== '30') n++;
        if (!inclureEchus) n++;
        if (filterType !== 'all') n++;
        if (hasPeriode) n++;
        return n;
    }, [filterAgence, jours, inclureEchus, filterType, hasPeriode]);

    const clearAllFilters = () => {
        setSearch('');
        setFilterAgence('all');
        setJours('30');
        setInclureEchus(true);
        setFilterType('all');
        setFilterDates(null);
    };

    // ─── Exports ──────────────────────────────────────────────────────
    const exportHeaders = ['N° Police', 'Client', 'Téléphone', 'Type', 'Compagnie', 'Échéance', 'Jours restants', 'Prime TTC'];

    const fetchAllForExport = async () => {
        const hide = message.loading("Préparation de l'export...", 0);
        try {
            const res = await contratsService.getEcheancesListe(buildParams(1, 99999));
            const payload = res?.data || res || {};
            const all = Array.isArray(payload) ? payload : payload.items || [];
            hide();
            return all;
        } catch (e) {
            hide();
            toast.error("Erreur lors de la préparation de l'export");
            return [];
        }
    };

    const buildExportData = (list) => list.map((c) => {
        const j = joursRestants(c.dateEcheance);
        return {
            police: c.numeroPolice || '',
            client: clientName(c.client),
            telephone: formatPhone(c.client?.telephone),
            type: c.typeAssurance?.nom || '',
            compagnie: c.compagnie?.nom || '',
            echeance: c.dateEcheance ? new Date(c.dateEcheance).toLocaleDateString('fr-FR') : '',
            jours: j === null ? '' : (j < 0 ? `Echu (${Math.abs(j)} j)` : `${j} j`),
            prime: formatMontantPdf(c.primeTTC || 0),
            primeNum: c.primeTTC || 0,
        };
    });

    // Libellé de fenêtre sans le caractère « ≤ » (absent de la police PDF standard).
    const windowLabel = () => {
        const raw = WINDOW_OPTIONS.find((o) => o.value === jours)?.label || '';
        return raw.replace('≤', '<=');
    };

    // Ligne « périmètre » des exports : période d'échéance si choisie, sinon fenêtre glissante
    const perimetreLabel = () => (hasPeriode
        ? `Échéances du ${filterDates[0].format('DD/MM/YYYY')} au ${filterDates[1].format('DD/MM/YYYY')}`
        : `Fenêtre : ${windowLabel()}${inclureEchus ? ' (échus inclus)' : ''}`);

    const exportTitle = () => {
        const typeNom = filterType !== 'all'
            ? ((typesAssurance || []).find((t) => t._id === filterType)?.nom || '')
            : '';
        return typeNom
            ? `ÉCHÉANCIER — ${typeNom.toUpperCase()}`
            : 'ÉCHÉANCIER — CONTRATS À RENOUVELER';
    };

    const handleExportPDF = async () => {
        const all = await fetchAllForExport();
        if (!all.length) { toast.info('Aucune échéance à exporter'); return; }
        const doc = new jsPDF({ orientation: 'landscape' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;

        doc.addImage(logoCap, 'JPEG', 14, 8, 40, 16);
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, pageWidth - 14, 15, { align: 'right' });

        const agenceNom = resolveAgenceLabel({ selected: filterAgence, agences, canSwitchAgence, userAgenceId });
        doc.setFontSize(12);
        doc.text(`AGENCE : ${agenceNom}`, centerX, 15, { align: 'center' });

        doc.setFontSize(14);
        doc.setFont(undefined, 'bolditalic');
        doc.text(exportTitle(), centerX, 30, { align: 'center' });
        doc.setFont(undefined, 'normal');

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(perimetreLabel(), centerX, 38, { align: 'center' });
        doc.setFont(undefined, 'normal');

        const data = buildExportData(all);
        autoTable(doc, {
            startY: 44,
            margin: { left: 14, right: 14 },
            tableWidth: 'wrap',
            head: [exportHeaders],
            body: data.map((d) => [d.police, d.client, d.telephone, d.type, d.compagnie, d.echeance, d.jours, d.prime]),
            styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak', lineColor: [0, 0, 0], lineWidth: 0.3, fillColor: false },
            headStyles: PDF_HEAD_STYLES,
            footStyles: PDF_FOOT_STYLES,
            alternateRowStyles: { fillColor: false },
            columnStyles: {
                0: { cellWidth: 32 },              // N° Police
                1: { cellWidth: 50 },              // Client
                2: { cellWidth: 26 },              // Téléphone
                3: { cellWidth: 38 },              // Type
                4: { cellWidth: 34 },              // Compagnie
                5: { cellWidth: 22, halign: 'center' }, // Échéance
                6: { cellWidth: 25, halign: 'center' }, // Jours restants
                7: { cellWidth: 30, halign: 'right' },  // Prime TTC
            },
        });

        addExporterFooter(doc, exporterName);
        doc.save(`echeances_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    const handleExportExcel = async () => {
        const all = await fetchAllForExport();
        if (!all.length) { toast.info('Aucune échéance à exporter'); return; }
        const data = buildExportData(all);
        const today = new Date().toLocaleDateString('fr-FR');
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Échéances');
        ws.columns = [{ width: 22 }, { width: 28 }, { width: 18 }, { width: 20 }, { width: 20 }, { width: 14 }, { width: 16 }, { width: 16 }];
        await addExcelLogoHeader(wb, ws);

        const titleRow = ws.addRow([exportTitle()]);
        ws.mergeCells(titleRow.number, 1, titleRow.number, exportHeaders.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        const windowRow = ws.addRow([perimetreLabel()]);
        ws.mergeCells(windowRow.number, 1, windowRow.number, exportHeaders.length);
        windowRow.getCell(1).font = { bold: true, italic: true, size: 11, color: { argb: '8B0000' } };
        windowRow.getCell(1).alignment = { horizontal: 'center' };

        addExcelExportInfo(ws, { colCount: exportHeaders.length, exporterName, today });
        ws.addRow([]);

        const headerRow = ws.addRow(exportHeaders);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: '000000' }, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        data.forEach((d) => {
            const row = ws.addRow([d.police, d.client, d.telephone, d.type, d.compagnie, d.echeance, d.jours, d.primeNum]);
            const primeCell = row.getCell(exportHeaders.length); // colonne « Prime TTC »
            primeCell.numFmt = '#,##0';
            primeCell.alignment = { horizontal: 'right' };
        });
        borderExcelTable(ws, headerRow.number, exportHeaders.length);
        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `echeances_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    const exportMenuItems = [
        { key: 'pdf', label: 'Exporter en PDF', icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#389e0d' }} />, onClick: handleExportExcel },
    ];

    // ─── Colonnes ─────────────────────────────────────────────────────
    const columns = [
        {
            title: 'N° Police',
            dataIndex: 'numeroPolice',
            key: 'police',
            width: 150,
            render: (t, r) => (
                <button type="button" className="ech-cell-police" onClick={() => navigate(`/dashboard/contrat/${r._id}`)}>
                    {t || '—'}
                </button>
            ),
        },
        {
            title: 'Client',
            key: 'client',
            width: 260,
            ellipsis: true,
            render: (_, r) => {
                const c = r.client;
                if (!c) return '—';
                const isEntreprise = c.type === 'entreprise';
                return (
                    <div className="ech-cell-client">
                        <div className={`ech-cell-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                            {isEntreprise ? <BankOutlined /> : <UserOutlined />}
                        </div>
                        <div className="ech-cell-client-body">
                            <div className="ech-cell-name">{clientName(c)}</div>
                            {c.telephone ? (
                                <a className="ech-cell-phone" href={`tel:${c.telephone}`} onClick={(e) => e.stopPropagation()}>
                                    <PhoneOutlined /> {formatPhone(c.telephone)}
                                </a>
                            ) : (
                                <span className="ech-cell-phone ech-cell-phone--empty"><PhoneOutlined /> —</span>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Type',
            dataIndex: ['typeAssurance', 'nom'],
            key: 'type',
            width: 150,
            ellipsis: true,
            responsive: ['lg'],
            render: (v) => v ? <Tag color="blue">{v}</Tag> : '—',
        },
        {
            title: 'Compagnie',
            dataIndex: ['compagnie', 'nom'],
            key: 'compagnie',
            width: 160,
            ellipsis: true,
            responsive: ['xl'],
            render: (v) => v || '—',
        },
        {
            title: 'Échéance',
            dataIndex: 'dateEcheance',
            key: 'dateEcheance',
            width: 130,
            defaultSortOrder: 'ascend',
            sorter: (a, b) => new Date(a.dateEcheance || 0) - new Date(b.dateEcheance || 0),
            render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—',
        },
        {
            title: 'Reste',
            key: 'reste',
            width: 120,
            sorter: (a, b) => (joursRestants(a.dateEcheance) ?? 1e9) - (joursRestants(b.dateEcheance) ?? 1e9),
            render: (_, r) => {
                const meta = echeanceMeta(joursRestants(r.dateEcheance));
                return <Tag color={meta.color}>{meta.label}</Tag>;
            },
        },
        {
            title: 'Prime TTC',
            dataIndex: 'primeTTC',
            key: 'primeTTC',
            width: 140,
            align: 'right',
            responsive: ['md'],
            sorter: (a, b) => (a.primeTTC || 0) - (b.primeTTC || 0),
            render: (v) => <span className="ech-cell-amount">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 190,
            align: 'center',
            fixed: 'right',
            render: (_, r) => (
                <div className="ech-actions">
                    <Tooltip title="Voir le contrat">
                        <Button
                            size="small"
                            icon={<SearchOutlined />}
                            className="ech-action-detail"
                            onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/contrat/${r._id}`); }}
                        >
                            Détail
                        </Button>
                    </Tooltip>
                    <Can perm="contrats:renouveler">
                        <Tooltip title="Renouveler ce contrat">
                            <Button
                                type="primary"
                                size="small"
                                icon={<ReloadOutlined />}
                                className="ech-action-renew"
                                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/nouveau-contrat?renew=${r._id}`); }}
                            >
                                Renouveler
                            </Button>
                        </Tooltip>
                    </Can>
                </div>
            ),
        },
    ];

    return (
        <div className="echeances-page">
            {/* Header */}
            <header className="echeances-header">
                <div className="echeances-header-text">
                    <h1 className="echeances-title">
                        <FieldTimeOutlined /> Échéances
                    </h1>
                    <p className="echeances-subtitle">Contrats à renouveler — à appeler pour relance</p>
                </div>
                <div className="echeances-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        {!isMobile && 'Guide'}
                    </Button>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Échéances &amp; renouvellements</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={620}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Cette page liste les contrats dont la <strong>date d'échéance approche</strong> (ou est
                    déjà dépassée sans renouvellement) : elle sert à <strong>appeler les clients</strong> pour
                    qu'ils renouvellent.
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <ClockCircleOutlined style={icStyle} />
                                <div><strong>1 · Choisir la fenêtre</strong> — « ≤ 7 / 15 / 30 jours »… affiche
                                    toutes les échéances jusqu'à cette date (cumulatif). Cochez
                                    <strong> « Inclure les échus »</strong> pour voir aussi les contrats déjà
                                    dépassés non renouvelés. Vous pouvez aussi choisir une
                                    <strong> période d'échéance</strong> précise (du… au…) : elle remplace alors la
                                    fenêtre, et filtrer par <strong>type d'assurance</strong> (ex. Automobile).
                                </div>
                            </div>
                            <div style={liStyle}>
                                <PhoneOutlined style={icStyle} />
                                <div><strong>2 · Appeler le client</strong> — Le numéro est affiché sur chaque
                                    ligne ; cliquez dessus pour lancer l'appel.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <ReloadOutlined style={icStyle} />
                                <div><strong>3 · Renouveler</strong> — Le bouton <strong>« Renouveler »</strong>
                                    ouvre directement le renouvellement du contrat.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <DownloadOutlined style={icStyle} />
                                <div><strong>4 · Imprimer / Exporter</strong> — Le bouton « Exporter » génère un
                                    PDF ou un Excel de l'échéancier filtré, à imprimer pour les appels.
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs */}
            <section className="echeances-kpis" aria-label="Indicateurs échéances">
                <MiniKpiCard icon={<FieldTimeOutlined />} label={hasPeriode ? 'Total (période)' : 'Total (fenêtre)'} value={stats.total} tone="blue" loading={loading} />
                <MiniKpiCard icon={<WarningOutlined />} label="Déjà échus" value={stats.echus} tone="red" loading={loading} />
                <MiniKpiCard icon={<ClockCircleOutlined />} label="≤ 7 jours" value={stats.urgent7} tone="orange" loading={loading} />
                <MiniKpiCard icon={<CalendarOutlined />} label="≤ 30 jours" value={stats.dans30} tone="green" loading={loading} />
            </section>

            {/* Carte principale */}
            <Card className="echeances-main-card">
                <div className="echeances-toolbar">
                    <Input
                        allowClear
                        placeholder="Rechercher (n° police, client, téléphone...)"
                        prefix={<SearchOutlined style={{ color: 'var(--cap-text-muted)' }} />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="echeances-search"
                        aria-label="Rechercher une échéance"
                    />
                    <Select value={jours} onChange={setJours} className="echeances-filter" aria-label="Fenêtre d'échéance" disabled={hasPeriode}>
                        {WINDOW_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                    </Select>
                    {canSwitchAgence && (
                        <Select value={filterAgence} onChange={setFilterAgence} className="echeances-filter" aria-label="Filtrer par agence">
                            <Option value="all">Toutes les agences</Option>
                            {agences.map((a) => <Option key={a._id} value={a._id}>{a.nom}</Option>)}
                        </Select>
                    )}
                    <Select value={filterType} onChange={setFilterType} className="echeances-filter" aria-label="Filtrer par type d'assurance">
                        <Option value="all">Tous les types d'assurance</Option>
                        {(typesAssurance || []).map((t) => <Option key={t._id} value={t._id}>{t.nom}</Option>)}
                    </Select>
                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Échéance début', 'Échéance fin']}
                        onChange={setFilterDates}
                        value={filterDates}
                        className="echeances-filter-dates"
                        allowClear
                    />
                    <Checkbox
                        checked={inclureEchus}
                        onChange={(e) => setInclureEchus(e.target.checked)}
                        className="echeances-check-echus"
                        disabled={hasPeriode}
                    >
                        Inclure les échus
                    </Checkbox>
                    {(activeFiltersCount > 0 || search) && (
                        <Button icon={<ClearOutlined />} onClick={clearAllFilters} className="echeances-btn-clear">
                            Effacer
                        </Button>
                    )}
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} className="echeances-btn-export">Exporter</Button>
                    </Dropdown>
                </div>

                {isMobile ? (
                    <Spin spinning={loading}>
                        {echeances.length === 0 ? (
                            <Empty
                                image={<CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />}
                                description={<span style={{ color: '#389e0d', fontWeight: 500 }}>Aucune échéance sur cette période</span>}
                                style={{ padding: 32 }}
                            />
                        ) : (
                            <List
                                dataSource={echeances}
                                rowKey="_id"
                                split={false}
                                renderItem={(r) => {
                                    const c = r.client || {};
                                    const isEntreprise = c.type === 'entreprise';
                                    const meta = echeanceMeta(joursRestants(r.dateEcheance));
                                    return (
                                        <Card
                                            key={r._id}
                                            size="small"
                                            className={`echeances-mobile-card reste-${meta.tone}`}
                                            onClick={() => navigate(`/dashboard/contrat/${r._id}`)}
                                        >
                                            <div className="mobile-card-row">
                                                <div className={`ech-cell-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                                                    {isEntreprise ? <BankOutlined /> : <UserOutlined />}
                                                </div>
                                                <div className="mobile-card-body">
                                                    <div className="mobile-card-head">
                                                        <span className="mobile-card-police">{r.numeroPolice || '—'}</span>
                                                        <Tag color={meta.color}>{meta.label}</Tag>
                                                    </div>
                                                    <div className="mobile-card-name">{clientName(c)}</div>
                                                    {c.telephone && (
                                                        <a className="mobile-card-phone" href={`tel:${c.telephone}`} onClick={(e) => e.stopPropagation()}>
                                                            <PhoneOutlined /> {formatPhone(c.telephone)}
                                                        </a>
                                                    )}
                                                    <div className="mobile-card-footer">
                                                        <span className="ech-cell-amount">{formatMontant(r.primeTTC || 0)}</span>
                                                        <span className="mobile-card-echeance">
                                                            <CalendarOutlined /> {r.dateEcheance ? new Date(r.dateEcheance).toLocaleDateString('fr-FR') : '—'}
                                                        </span>
                                                    </div>
                                                    <Can perm="contrats:renouveler">
                                                        <Button
                                                            block
                                                            type="primary"
                                                            size="small"
                                                            icon={<ReloadOutlined />}
                                                            className="ech-action-renew"
                                                            style={{ marginTop: 8 }}
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/nouveau-contrat?renew=${r._id}`); }}
                                                        >
                                                            Renouveler
                                                        </Button>
                                                    </Can>
                                                </div>
                                            </div>
                                        </Card>
                                    );
                                }}
                            />
                        )}
                        <div className="echeances-pagination-wrapper">
                            <Pagination
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                total={pagination.total}
                                onChange={(p, ps) => fetchEcheances(p, ps)}
                                size="small"
                                simple
                            />
                        </div>
                    </Spin>
                ) : (
                    <Table
                        dataSource={echeances}
                        columns={columns}
                        rowKey="_id"
                        loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                        pagination={{
                            ...pagination,
                            onChange: (p, ps) => fetchEcheances(p, ps),
                            showSizeChanger: true,
                            showTotal: (t, range) => `${range[0]}-${range[1]} sur ${t} échéance${t > 1 ? 's' : ''}`,
                        }}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        locale={{
                            emptyText: loading ? (
                                <div style={{ minHeight: 220 }} />
                            ) : (
                                <Empty
                                    image={<CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />}
                                    description={<span style={{ color: '#389e0d', fontWeight: 500 }}>Aucune échéance sur cette période</span>}
                                />
                            ),
                        }}
                    />
                )}
            </Card>
        </div>
    );
};

export default Echeances;
